import React from 'react';
import { Phone, Mail, MapPin, Star, Clock } from 'lucide-react';
import type { SiteConfig } from '../types';

interface BusinessProfileProps {
  config: SiteConfig;
}

const BusinessProfile: React.FC<BusinessProfileProps> = ({ config }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{config.header.title}</h1>
              <p className="text-gray-600 mt-1">{config.header.tagline}</p>
            </div>
            <div className="flex items-center space-x-6">
              <a 
                href={`tel:${config.header.phone}`}
                className="flex items-center text-gray-600 hover:text-blue-600"
              >
                <Phone className="w-5 h-5 mr-2" />
                {config.header.phone}
              </a>
              <a
                href={`mailto:${config.header.email}`}
                className="flex items-center text-gray-600 hover:text-blue-600"
              >
                <Mail className="w-5 h-5 mr-2" />
                {config.header.email}
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div 
        className="relative h-96 bg-cover bg-center"
        style={{ backgroundImage: `url(${config.hero.image})` }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-center">
          <div className="max-w-3xl px-4">
            <h2 className="text-4xl font-bold text-white mb-4">
              {config.hero.headline}
            </h2>
            <p className="text-xl text-white">
              {config.hero.subheadline}
            </p>
          </div>
        </div>
      </div>

      {/* Services */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Featured Services
          </h2>
          {config.services?.featured && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {config.services.featured.map((service, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {service.name}
                  </h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              ))}
            </div>
          )}
          
          {/* Service Categories */}
          <div className="mt-16">
            {config.services?.categories && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {config.services.categories.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {category.name}
                    </h3>
                    <p className="text-gray-600">{category.description}</p>
                    {category.items?.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Services Included:
                        </h4>
                        <div className="space-y-2">
                          {category.items.map((item, itemIndex) => (
                            <div key={itemIndex} className="text-gray-600">
                              • {item.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {category.brands?.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Brands Serviced:
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {category.brands.map((brand, brandIndex) => (
                            <span
                              key={brandIndex}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                            >
                              {brand}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reviews */}
      {config.reviews.enabled && (
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Customer Reviews
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {config.reviews.featured.map((review, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="flex items-center mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4">{review.text}</p>
                  <p className="text-gray-900 font-medium">{review.author}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* About */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {config.about.title}
              </h2>
              <p className="text-gray-600 leading-relaxed">
                {config.about.content}
              </p>
            </div>
            <div>
              <img
                src={config.about.image}
                alt="About us"
                className="rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {config.faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Contact Us
              </h3>
              <div className="space-y-3">
                <a 
                  href={`tel:${config.header.phone}`}
                  className="flex items-center text-gray-600 hover:text-blue-600"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {config.header.phone}
                </a>
                <a
                  href={`mailto:${config.header.email}`}
                  className="flex items-center text-gray-600 hover:text-blue-600"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  {config.header.email}
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Business Hours
              </h3>
              <div className="space-y-2">
                <div className="flex items-center text-gray-600">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>Mon-Fri: 8:00 AM - 6:00 PM</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>Sat: 9:00 AM - 2:00 PM</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Service Area
              </h3>
              <div className="flex items-start text-gray-600">
                <MapPin className="w-5 h-5 mr-2 mt-1" />
                <p>Serving the greater metropolitan area and surrounding suburbs</p>
              </div>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t text-center text-gray-500">
            <p>© {new Date().getFullYear()} {config.header.title}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BusinessProfile;